import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import AudioPlayer from "@/components/AudioPlayer";
import ApplicationModal from "@/components/ApplicationModal";
import { ArrowLeft, ExternalLink, Instagram, Heart } from "lucide-react";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/") ? `${API_ORIGIN}${u}` : u);

export default function ArtistProfile() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [state, setState] = useState("loading");
  const [adoptOpen, setAdoptOpen] = useState(false);

  useEffect(() => {
    setState("loading");
    api.get(`/artists/${slug}`)
      .then((r) => { setArtist(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") {
    return (
      <main className="min-h-screen flex items-center justify-center" data-testid="artist-loading">
        <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-fg/50">Chargement…</div>
      </main>
    );
  }
  if (state === "notfound") {
    return (
      <main className="min-h-screen pt-40 px-6 md:px-12 max-w-[1200px] mx-auto" data-testid="artist-notfound">
        <div className="meta-label text-cinabre">§ 404</div>
        <h1 className="font-serif text-6xl mt-4">Artiste introuvable.</h1>
        <Link to="/artistes" className="btn-editorial ghost mt-8"><ArrowLeft size={14} /> Retour aux artistes</Link>
      </main>
    );
  }

  const works = artist.works || [];
  const audio = works.filter((w) => w.kind === "audio" || w.kind === "midi");
  const images = works.filter((w) => w.kind === "image");
  const texts = works.filter((w) => w.kind === "text");

  return (
    <main className="pt-32 pb-20" data-testid="artist-profile" data-slug={artist.slug}>
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1600px] mx-auto grid md:grid-cols-12 gap-10 items-end">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="md:col-span-7"
        >
          <Link to="/artistes" className="meta-label text-fg/60 link-underline"><ArrowLeft size={12} className="inline mr-1" /> Tous les artistes</Link>
          <div className="meta-label text-cinabre mt-6">§ {artist.medium || "Artiste"} · Site {artist.site || "—"}</div>
          <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight mt-6" data-testid="artist-name">
            {artist.name}
          </h1>
          {(artist.website || artist.instagram) && (
            <div className="mt-8 flex gap-6 items-center flex-wrap">
              {artist.website && (
                <a href={artist.website} target="_blank" rel="noreferrer" className="link-underline flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase">
                  <ExternalLink size={14} /> Site web
                </a>
              )}
              {artist.instagram && (
                <a href={artist.instagram} target="_blank" rel="noreferrer" className="link-underline flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase">
                  <Instagram size={14} /> Instagram
                </a>
              )}
            </div>
          )}

          {/* Bouton "J'adopte {Prénom Nom}" — ouvre le modal Adoption préconfiguré */}
          <div className="mt-10">
            <button
              type="button"
              onClick={() => setAdoptOpen(true)}
              className="inline-flex items-center gap-3 bg-cinabre text-bg px-7 py-4 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors group"
              data-testid="adopt-artist-cta"
            >
              <Heart size={14} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />
              J'adopte {artist.name}
            </button>
            <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50">
              3 min · aucune obligation · échange direct
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.65, 0, 0.35, 1] }}
          className="md:col-span-5 aspect-[4/5] overflow-hidden border border-fg/15"
        >
          {artist.image ? (
            <img src={artist.image} alt={artist.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-fg/5 flex items-center justify-center font-mono text-[10px] tracking-[0.24em] uppercase text-fg/40">Portrait à venir</div>
          )}
        </motion.div>
      </section>

      {/* Bio */}
      {artist.bio && (
        <section className="mt-24 px-6 md:px-12 max-w-[1200px] mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="meta-label text-cinabre">§ Bio</div>
          </div>
          <div className="md:col-span-9">
            <p className="font-serif text-2xl md:text-3xl leading-[1.4] text-fg/90 whitespace-pre-line" data-testid="artist-bio">{artist.bio}</p>
          </div>
        </section>
      )}

      {/* Audio & MIDI works */}
      {audio.length > 0 && (
        <section className="mt-24 px-6 md:px-12 max-w-[1200px] mx-auto" data-testid="artist-audio-section">
          <div className="meta-label text-cinabre mb-8">§ Écoutes {audio.some(w => w.kind === "midi") ? "· Slam · MIDI" : "· Slam · Audio"}</div>
          <div className="space-y-4">
            {audio.map((w) => (
              <div key={w.id}>
                <AudioPlayer src={toAbs(w.url)} title={w.title || "Sans titre"} kind={w.kind} />
                {w.description && <p className="mt-3 text-fg/70 leading-relaxed max-w-2xl">{w.description}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Text works */}
      {texts.length > 0 && (
        <section className="mt-24 px-6 md:px-12 max-w-[1200px] mx-auto" data-testid="artist-texts-section">
          <div className="meta-label text-cinabre mb-8">§ Textes · Écritures</div>
          <div className="space-y-16">
            {texts.map((w) => (
              <article key={w.id} className="border-l-2 border-cinabre pl-8">
                {w.title && <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">{w.title}</h3>}
                <div className="mt-6 font-serif text-xl md:text-2xl leading-[1.55] text-fg/85 whitespace-pre-wrap">{w.text}</div>
                {w.description && <div className="mt-4 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{w.description}</div>}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Image gallery */}
      {images.length > 0 && (
        <section className="mt-24 px-6 md:px-12 max-w-[1600px] mx-auto" data-testid="artist-gallery">
          <div className="meta-label text-cinabre mb-8">§ Œuvres · Images</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {images.map((w) => (
              <figure key={w.id} className="border border-fg/15 overflow-hidden">
                <img src={toAbs(w.url)} alt={w.title || artist.name} className="w-full aspect-[4/5] object-cover" />
                {(w.title || w.description) && (
                  <figcaption className="p-4">
                    {w.title && <div className="font-serif text-lg">{w.title}</div>}
                    {w.description && <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 mt-1">{w.description}</div>}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </section>
      )}

      {works.length === 0 && !artist.bio && (
        <section className="mt-20 px-6 md:px-12 max-w-[1200px] mx-auto">
          <div className="border border-fg/15 p-10 md:p-16 text-center">
            <div className="meta-label text-cinabre">§ Bientôt</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl italic max-w-2xl mx-auto">Le contenu de cette fiche sera dévoilé prochainement.</p>
          </div>
        </section>
      )}

      {/* Footer meta */}
      <section className="mt-24 px-6 md:px-12 max-w-[1200px] mx-auto border-t border-fg/15 pt-10">
        <div className="grid md:grid-cols-3 gap-6 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
          <div><span className="text-fg/40">Médium</span><div className="text-fg mt-1 tracking-[0.16em]">{artist.medium || "—"}</div></div>
          <div><span className="text-fg/40">Site</span><div className="text-fg mt-1 tracking-[0.16em]">{artist.site || "—"}</div></div>
          <div><span className="text-fg/40">URL</span><div className="text-fg mt-1 tracking-[0.16em]">/artistes/{artist.slug}</div></div>
        </div>
      </section>

      {/* Modal Adoption préconfiguré avec le nom de l'artiste */}
      <ApplicationModal
        open={adoptOpen}
        onClose={() => setAdoptOpen(false)}
        kind="adoption_artiste"
        artistName={artist.name}
      />
    </main>
  );
}
